import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import { Chip, Tooltip, Typography } from '@material-ui/core';
import { EndConditions } from '@pvplevelup/core/dist/interfaces/Game/IGame';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins

const TEAM_COLORS = ['rgb(62, 124, 255)', 'rgb(228, 54, 55)'];

const useStyles = makeStyles(theme => ({
  root: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'center',
    gridGap: theme.spacing(2),
  },
  title: {
    fontWeight: 800,
    lineHeight: 1.2,
  },
  zone: {
    fontWeight: 400,
    opacity: 0.6,
  },
  chips: {
    marginTop: theme.spacing(0.75),
    '& > *': {
      marginRight: theme.spacing(0.75),
    },
  },
  right: {
    textAlign: 'right',
  },
  actions: {
    marginTop: theme.spacing(1.25),
  },
  timer: {
    fontWeight: 800,
    fontSize: '2.4rem',
    lineHeight: 1.1,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '0.02em',
  },
  timerLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    opacity: 0.55,
  },
  mmr: {
    display: 'inline-block',
    marginTop: theme.spacing(0.5),
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: '0.8rem',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
  },
}));

interface Props{
  map: string | undefined;
  zone: string | undefined;
  mode: string | undefined;
  endCondition: string | undefined;
  scoreToWin: number | undefined;
  time: number | undefined;
  gameTimer: string;
  teamMMR: number[] | undefined;
  teamCMMR: number[] | undefined;
  /** buttons under the map name, e.g. Show Game in Chat */
  actions?: React.ReactNode;
}


const GameHeaderComponent: React.FC<Props> = ({map,zone,mode,endCondition,scoreToWin,time,gameTimer,teamMMR,teamCMMR,actions}) => {
  const classes = useStyles();

  let team1MMR = teamMMR && teamMMR[0] != null && teamCMMR && teamCMMR[0] != null ? ((teamMMR[0] + teamCMMR[0]) / 2) : 0;
  let team2MMR = teamMMR && teamMMR[1] != null && teamCMMR && teamCMMR[1] != null ? ((teamMMR[1] + teamCMMR[1]) / 2) : 0;
  const favored = team1MMR > team2MMR ? 0 : team2MMR > team1MMR ? 1 : null;

  return (
    <div className={classes.root}>
      <div style={{minWidth: 0}}>
        <Typography variant="h4" className={classes.title}>
          {map} {zone && <span className={classes.zone}>· {zone}</span>}
        </Typography>
        <div className={classes.chips}>
          {mode && <Chip size="small" label={mode} style={{fontWeight: 600}} />}
          {endCondition == EndConditions.Score && scoreToWin != null && <Chip size="small" variant="outlined" label={`First to ${scoreToWin}`} />}
          {endCondition == EndConditions.Time && time != null && <Chip size="small" variant="outlined" label={`${time} minutes`} />}
        </div>
        {actions && <div className={classes.actions}>{actions}</div>}
      </div>
      <div className={classes.right}>
        <div className={classes.timerLabel}>Game time</div>
        <div className={classes.timer}>{gameTimer}</div>
        <Tooltip title="Average MMR difference between the teams">
          <span className={classes.mmr}>
            MMR diff <b style={{color: favored != null ? TEAM_COLORS[favored] : undefined}}>{Math.round(Math.abs((team1MMR - team2MMR)))}</b>
          </span>
        </Tooltip>
      </div>
    </div>
  )
}

// Memoized: the game views re-render several times a second (position / team location ticks)
// while these props only change when the game data itself changes.
export const GameHeader = React.memo(GameHeaderComponent);
